'use strict';

(() => {
  const PRODUCT_RETURN_KEY = 'oluvie:product-return:v1';
  const PRODUCT_RETURN_TTL_MS = 120000;
  const unmodifiedPrimaryClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  if (typeof window === 'undefined' || customElements.get('oluvie-reference-product')) return;

  customElements.define('oluvie-reference-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.core = window.OluvieReferenceProductCore;
      this.optionLabel = window.OluvieOptionLabel;
      const configNode = this.querySelector('[data-or-config]');
      try {
        if (!this.core || !this.optionLabel) throw new Error('Product controller core is unavailable.');
        if (!configNode) throw new Error('Product configuration is unavailable.');
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        this.enableFallback(error);
        return;
      }
      this.initialized = true;
      this.dataset.initialized = 'true';
      this.abort = new AbortController();
      document.addEventListener('pointerdown', () => { this.dataset.orFocusInput = 'pointer'; }, { signal: this.abort.signal, capture: true });
      document.addEventListener('keydown', (event) => {
        if (!['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) this.dataset.orFocusInput = 'keyboard';
      }, { signal: this.abort.signal, capture: true });
      const fallback = this.querySelector('[data-or-fallback-variant]');
      const fallbackControl = fallback?.querySelector('[data-or-fallback-control]');
      if (fallbackControl) fallbackControl.disabled = true;
      if (fallback) fallback.hidden = true;
      const enhancedOptions = this.querySelector('[data-or-enhanced-options]');
      if (enhancedOptions) enhancedOptions.hidden = false;
      this.variants = this.config.variants;
      const explicitId = new URL(window.location.href).searchParams.get('variant');
      const nativeId = fallbackControl?.hasAttribute('data-or-native-selection-guard') ? fallbackControl.value : null;
      const requestedId = nativeId || explicitId;
      const state = this.core.initialState(this.variants, this.config.initial, requestedId);
      this.initial = state.initial;
      this.sizeIndex = this.config.sizeIndex;
      this.colorIndex = this.config.colorIndex;
      this.choices = state.choices;
      this.selectedVariant = this.core.selectVariant(this.variants, state.selectedVariantId);
      const explicitVariant = this.variants.some((variant) => String(variant.id) === String(requestedId));
      if (this.sizeIndex >= 0 && !explicitVariant) {
        this.choices[this.sizeIndex] = null;
        this.selectedVariant = null;
      }
      this.mediaSignature = null;
      this.failedMediaKeys = new Set();
      this.genericMediaNote = this.querySelector('[data-or-media-note]')?.textContent || '';
      this.selectedQuantity = 1;
      this.busy = false;
      this.uncertain = false;
      this.added = false;
      this.addFeedback = false;
      this.cartSyncGeneration = this.cartSyncGeneration || 0;
      this.cartQuantities = new Map();
      for (const line of this.config.cartLines || []) this.cartQuantities.set(line.variantId, this.core.cartQuantity(this.config.cartLines, line.variantId));
      this.tierCart = this.config.cartState || null;
      this.tierAdded = false;
      this.tierCartPending = false;
      this.tierSelectionChanged = false;
      this.tierCartVersion = 0;
      this.moneyFormatter = new Intl.NumberFormat(this.config.locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      this.form = this.querySelector('form');
      const back = this.querySelector('[data-or-back]');
      this.setupReturnNavigation(back);
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.abort.signal });
      this.querySelector('[data-or-media-track]')?.addEventListener('keydown', (event) => this.onMediaKeydown(event), { signal: this.abort.signal });
      this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.add(event.submitter || document.activeElement); }, { signal: this.abort.signal });
      this.querySelector('[data-or-zoom]')?.addEventListener('close', () => {
        const trigger = this.zoomTrigger?.closest('[data-or-media]')?.hidden
          ? this.querySelector('[data-or-media]:not([hidden]) [data-or-zoom-open]') || this.querySelector('[data-or-media-track]')
          : this.zoomTrigger;
        trigger?.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      window.addEventListener('resize', () => { this.syncColorRail(); this.syncQuantityPlacement(); if (this.querySelector('[data-or-zoom]')?.open) this.renderZoom(); }, { signal: this.abort.signal });
      document.addEventListener('oluvie:cart-updated', (event) => {
        const cart = event.detail?.cart;
        if (!Array.isArray(cart?.items)) return;
        this.tierCartVersion += 1;
        this.cartQuantities = new Map(cart.items.map((line) => [line.variant_id ?? line.id, this.core.cartQuantity(cart.items, line.variant_id ?? line.id)]));
        this.syncCart(cart);
      }, { signal: this.abort.signal });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.productViewIdentity = null;
        this.restoreSelection(history.state);
        const generation = ++this.cartSyncGeneration;
        const tierVersion = this.tierCartVersion;
        this.tierCart = null;
        this.tierCartPending = false;
        this.renderTier();
        void this.getCart().then((cart) => { if (this.isConnected && generation === this.cartSyncGeneration && tierVersion === this.tierCartVersion) this.syncCart(cart); }).catch(() => {
          if (!this.isConnected || generation !== this.cartSyncGeneration || tierVersion !== this.tierCartVersion) return;
          this.tierCart = null;
          this.tierCartPending = false;
          this.renderTier();
        });
      }, { signal: this.abort.signal });
      this.setupToolbar();
      this.setupFavorites();
      this.setupOptionSheet();
      this.setupSizeGuide();
      this.setupMediaFallback();
      this.observeMedia();
      const thumbnails = this.querySelector('[data-or-color-thumbnails]');
      if (thumbnails) {
        thumbnails.hidden = false;
        for (const image of thumbnails.querySelectorAll('img')) {
          const unavailable = () => { image.hidden = true; image.closest('button')?.classList.add('or-product__color-thumbnail--text'); };
          image.addEventListener('error', unavailable, { signal: this.abort.signal });
          if (image.complete && image.naturalWidth === 0) unavailable();
        }
      }
      window.addEventListener('popstate', (event) => this.restoreSelection(event.state), { signal: this.abort.signal });
      this.restoreSelection(history.state, nativeId);
      this.saveSelectionHistory(false);
    }

    enableFallback(error) {
      this.dataset.initialized = 'fallback';
      const fallback = this.querySelector('[data-or-fallback-variant]');
      const fallbackControl = fallback?.querySelector('[data-or-fallback-control]');
      if (fallback) fallback.hidden = false;
      if (fallbackControl) fallbackControl.disabled = false;
      console.error('Oluvie product controller could not initialize; native variant fallback enabled.', error);
    }

    disconnectedCallback() {
      if (this.sizeGuide?.open) this.sizeGuide.close();
      if (this.optionSheet?.open) this.optionSheet.close();
      this.abort?.abort();
      const colorTrack = this.querySelector('[data-or-color-track]');
      if (colorTrack) delete colorTrack.dataset.bound;
      clearTimeout(this.addFeedbackTimer);
      this.mediaObserver?.disconnect();
      if (this.toolbarFrame) cancelAnimationFrame(this.toolbarFrame);
      this.toolbarFrame = null;
      this.cartSyncGeneration = (this.cartSyncGeneration || 0) + 1;
      this.productViewIdentity = null;
      this.initialized = false;
    }

    format(cents) {
      if (this.config.locale.toLowerCase().startsWith('nl')) return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100).replace(/\s+/g, ' ');
      const symbols = { EUR: '€', USD: '$', GBP: '£' };
      return `${this.moneyFormatter.format((Number(cents) || 0) / 100)} ${symbols[this.config.currency] || this.config.currency}`;
    }

    normalizeReturnState(value) {
      if (!value || value.productPath !== location.pathname) return null;
      const href = this.core.backUrl(value.href, location.origin, location.href, '');
      const depth = Number(value.depth);
      if (!href || !Number.isSafeInteger(depth) || depth < 0 || depth > 100) return null;
      return { href, productPath: location.pathname, depth };
    }

    consumeReturnBridge() {
      let raw = null;
      try {
        raw = sessionStorage.getItem(PRODUCT_RETURN_KEY);
        sessionStorage.removeItem(PRODUCT_RETURN_KEY);
      } catch (_) {
        return null;
      }
      if (!raw || history.length <= 1) return null;
      try {
        const value = JSON.parse(raw);
        const now = Date.now();
        if (!Number.isFinite(value?.at) || value.at > now + 5000 || now - value.at > PRODUCT_RETURN_TTL_MS || value.to !== location.pathname) return null;
        const from = new URL(value.from, location.origin);
        const referrer = new URL(document.referrer, location.origin);
        if (from.origin !== location.origin || referrer.origin !== location.origin || from.pathname !== referrer.pathname || from.search !== referrer.search) return null;
        const href = this.core.backUrl(from.href, location.origin, location.href, '');
        return href ? { href, productPath: location.pathname, depth: 0 } : null;
      } catch (_) {
        return null;
      }
    }

    setupReturnNavigation(back) {
      if (!back) return;
      const fallback = back.getAttribute('href');
      let state = this.normalizeReturnState(history.state?.oluvieProductReturn);
      if (!state) state = this.consumeReturnBridge();
      if (!state) {
        back.href = this.core.backUrl(document.referrer, location.origin, location.href, fallback);
        return;
      }
      history.replaceState({ ...history.state, oluvieProductReturn: state }, '', location.href);
      back.href = state.href;
    }

    returnToListing(event) {
      const back = event.target.closest('[data-or-back]');
      if (!back || !this.contains(back) || event.defaultPrevented || !unmodifiedPrimaryClick(event)) return false;
      const targetName = (back.getAttribute('target') || '').toLowerCase();
      if ((targetName && targetName !== '_self') || back.hasAttribute('download')) return false;
      const state = this.normalizeReturnState(history.state?.oluvieProductReturn);
      const steps = state ? state.depth + 1 : 0;
      if (!state || steps < 1 || history.length <= steps) return false;
      try {
        history.go(-steps);
        event.preventDefault();
        return true;
      } catch (_) {
        return false;
      }
    }

    onClick(event) {
      if (this.returnToListing(event)) return;
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || (this.sheetClosing && button.closest('[data-or-option-sheet]'))) return;
      if (button.hasAttribute('data-or-size-guide-open')) { this.openSizeGuide(); return; }
      if (button.hasAttribute('data-or-size-guide-close')) { this.sizeGuide?.close(); return; }
      if (button.hasAttribute('data-or-option-sheet-open')) { this.openOptionSheet(button); return; }
      if (button.hasAttribute('data-or-option-sheet-close')) { this.closeOptionSheet(); return; }
      if (button.hasAttribute('data-or-sheet-add')) { this.add(button); return; }
      if (button.hasAttribute('data-or-color-previous')) { this.scrollColors(-1); return; }
      if (button.hasAttribute('data-or-color-next')) { this.scrollColors(1); return; }
      if (button.hasAttribute('data-or-option-index')) { this.chooseOption(Number(button.dataset.orOptionIndex), button.dataset.orOptionValue); return; }
      if (button.hasAttribute('data-or-quantity')) { this.selectedQuantity = this.core.quantity(this.selectedQuantity + Number(button.dataset.orQuantity)); this.addFeedback = false; this.tierAdded = false; this.tierSelectionChanged = true; this.render(); button.closest('.or-product__quantity')?.scrollIntoView({ block: 'nearest' }); return; }
      if (button.dataset.orZoomOpen) { this.openZoom(button); return; }
      if (button.hasAttribute('data-or-zoom-close')) { button.closest('dialog')?.close(); return; }
      if (button.hasAttribute('data-or-zoom-previous')) { this.stepZoom(-1); return; }
      if (button.hasAttribute('data-or-zoom-next')) { this.stepZoom(1); return; }
      if (button.hasAttribute('data-or-share')) this.share();
      if (button.hasAttribute('data-or-save')) this.toggleFavorite();
    }

    chooseOption(index, value) {
      if (this.busy || this.uncertain || !this.optionAvailable(index, value)) return;
      const choices = [...this.choices];
      if (index === this.colorIndex && this.sizeIndex >= 0) choices[this.sizeIndex] = null;
      const result = this.core.chooseOption(this.variants, choices, index, value);
      this.choices = result.choices;
      this.selectedVariant = result.selectedVariantId ? this.core.selectVariant(this.variants, result.selectedVariantId) : null;
      this.selectedQuantity = 1;
      this.tierAdded = false;
      this.tierSelectionChanged = true;
      this.added = false;
      this.addFeedback = false;
      this.status('');
      this.render();
      this.saveSelectionHistory(true);
    }

    optionAvailable(index, value) {
      if (index === this.colorIndex) return this.core.optionAvailable(this.variants, this.choices, index, value);
      return this.core.compatible(this.variants, this.choices, index).some((variant) => variant.available && variant.options[index] === value);
    }

    restoreSelection(savedState, initialNativeId = null) {
      if (this.busy) { this.pendingHistorySelection = { state: savedState }; return; }
      const explicitId = initialNativeId || new URL(window.location.href).searchParams.get('variant');
      const state = this.core.initialState(this.variants, this.config.initial, explicitId);
      const saved = savedState?.oluvieProductSelection;
      const savedVariant = Array.isArray(saved?.choices) ? this.core.match(this.variants, saved.choices) : null;
      const savedIsValid = String(saved?.productId) === String(this.config.id)
        && (saved.variantId || null) === explicitId
        && String(savedVariant?.id || '') === String(saved.variantId || '')
        && Array.isArray(saved.choices) && saved.choices.length === state.choices.length
        && saved.choices.every((value, index) => value === null || this.variants.some((variant) => variant.options[index] === value));
      this.initial = state.initial;
      this.choices = savedIsValid ? [...saved.choices] : state.choices;
      if (!savedIsValid && this.sizeIndex >= 0 && !this.variants.some((variant) => String(variant.id) === explicitId)) this.choices[this.sizeIndex] = null;
      const matched = this.core.match(this.variants, this.choices);
      this.selectedVariant = matched?.available ? matched : null;
      this.selectedQuantity = 1;
      this.added = false;
      this.addFeedback = false;
      this.tierAdded = false;
      this.tierSelectionChanged = true;
      if (!this.uncertain) this.status('');
      this.render();
    }

    saveSelectionHistory(push) {
      const url = new URL(window.location.href);
      const resolved = this.core.match(this.variants, this.choices);
      const variantId = resolved ? String(resolved.id) : null;
      if (variantId) url.searchParams.set('variant', variantId); else url.searchParams.delete('variant');
      const selection = { productId: this.config.id, variantId, choices: [...this.choices] };
      const current = history.state?.oluvieProductSelection;
      const method = push && JSON.stringify(current) !== JSON.stringify(selection) ? 'pushState' : 'replaceState';
      const state = { ...history.state, oluvieProductSelection: selection };
      const productReturn = this.normalizeReturnState(history.state?.oluvieProductReturn);
      if (productReturn) {
        state.oluvieProductReturn = { ...productReturn, depth: productReturn.depth + (method === 'pushState' ? 1 : 0) };
      }
      history[method](state, '', url);
    }

    displayVariant() {
      return this.selectedVariant || this.core.match(this.variants, this.choices) || this.core.compatible(this.variants, this.choices).find((variant) => variant.available) || this.initial;
    }

    setupToolbar() {
      const toolbar = this.querySelector('.or-product__navigation');
      const gallery = this.querySelector('.or-product__gallery');
      if (!toolbar || !gallery) return;
      const sync = () => { toolbar.dataset.scrolled = String(gallery.getBoundingClientRect().bottom <= toolbar.offsetHeight); };
      const schedule = () => {
        if (this.toolbarFrame) return;
        this.toolbarFrame = requestAnimationFrame(() => { this.toolbarFrame = null; sync(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      sync();
    }

    readFavorites() {
      return this.cartCore.normalize(JSON.parse(localStorage.getItem('oluvie:saved-cart:v1') || '[]'));
    }

    setupFavorites() {
      this.cartCore = window.OluvieReferenceCartCore;
      if (!this.cartCore) return;
      const sync = () => {
        try { this.savedVariants = this.readFavorites(); }
        catch (_) { this.status(this.config.strings.storageError, true); }
        this.renderFavorite();
      };
      for (const button of this.querySelectorAll('[data-or-save]')) button.hidden = false;
      window.addEventListener('storage', (event) => { if (event.key === 'oluvie:saved-cart:v1' || event.key === null) sync(); }, { signal: this.abort.signal });
      window.addEventListener('pageshow', sync, { signal: this.abort.signal });
      sync();
    }

    renderFavorite() {
      this.favorited = Boolean(this.selectedVariant && this.cartCore?.find(this.savedVariants, this.selectedVariant.id));
      for (const button of this.querySelectorAll('[data-or-save]')) {
        button.disabled = this.busy || this.uncertain;
        button.setAttribute('aria-pressed', String(Boolean(this.favorited)));
        button.setAttribute('aria-label', this.favorited ? this.config.strings.unsave : this.config.strings.save);
      }
    }

    toggleFavorite() {
      if (!this.cartCore || this.busy || this.uncertain) return;
      if (!this.selectedVariant) {
        this.status(this.config.strings.chooseVariant, true);
        this.openOptionSheet(this.optionTrigger);
        this.firstMissingOption()?.focus({ preventScroll: true });
        return;
      }
      try {
        const values = this.readFavorites();
        const variant = this.selectedVariant;
        const saved = this.cartCore.find(values, variant.id);
        const url = new URL(this.config.productUrl, location.origin);
        url.searchParams.set('variant', variant.id);
        const media = this.querySelector('[data-or-media]:not([hidden]) img');
        const next = saved ? this.cartCore.remove(values, variant.id) : this.cartCore.save(values, {
          variantId: variant.id, quantity: this.selectedQuantity,
          handle: this.config.handle, title: this.config.title, variantTitle: variant.title,
          image: media?.currentSrc || media?.src || this.config.initialImage || '',
          url: `${url.pathname}${url.search}`, price: variant.price,
          hasOnlyDefaultVariant: this.config.hasOnlyDefaultVariant,
        });
        localStorage.setItem('oluvie:saved-cart:v1', JSON.stringify(next));
        this.savedVariants = next;
        this.renderFavorite();
      } catch (_) { this.status(this.config.strings.storageError, true); }
    }

    setupSizeGuide() {
      this.sizeGuide = this.querySelector('[data-or-size-guide]');
      this.sizeGuideTrigger = this.querySelector('[data-or-size-guide-open]');
      const content = this.querySelector('[data-or-size-guide-content]');
      const description = this.querySelector('[data-or-description] .rte');
      if (!this.sizeGuide || !this.sizeGuideTrigger || !content || !description) return;
      const guideLabel = /\b(maattabel(?:len)?|matentabel(?:len)?|size\s+(chart|guide)|größentabelle|grössentabelle|guide\s+des\s+tailles|tableau\s+des\s+tailles|tabellen?\b[^.]*\b(maten|cm|inch))\b/i;
      const sizeHeader = /\b(maat|maten|pakmaat|broekmaat|size|sizes|taille|uk|eu|us)\b/i;
      const measureHeader = /(borst|bust|chest|taille|waist|heup|hip|schouder|shoulder|mouw|sleeve|lengte|length|omtrek|omvang|binnenbeen|inseam|hoogte|height|zoom|hals|neck|rug|back)/i;
      const sizeValue = /^(?:(UK|EU|US)\s*)?([2-9]?X{1,3}[SL]|[SML]|\d{1,3}(\s*[-–/]\s*\d{1,3})?(\s*\([2-9]?X{1,3}[SL]\)|\s*\([SML]\))?)$/i;
      const text = (node) => node.textContent.trim();
      const numeric = (value) => /^\d+([.,]\d+)?(\s*[-–/]\s*\d+([.,]\d+)?)*$/.test(value.replace(/\b(cm|inch(es)?|in)\b|["″]/gi, '').trim());
      const nodes = [];
      const copiedContext = new Set();
      for (const table of description.querySelectorAll('table')) {
        if (table.parentElement.closest('table') || table.querySelector('table, img, picture, video, iframe, script, style')) continue;
        const rows = Array.from(table.rows);
        if (rows.length < 3) continue;
        const sizeColumns = [];
        const measureColumns = [];
        let column = 0;
        for (const cell of rows[0].cells) {
          for (let offset = 0; offset < cell.colSpan; offset += 1) {
            if (sizeHeader.test(text(cell))) sizeColumns.push(column + offset);
            if (measureHeader.test(text(cell)) && !/verpakking|pakket|package|parcel|shipping/i.test(text(cell))) measureColumns.push(column + offset);
          }
          column += cell.colSpan;
        }
        const sizes = new Set();
        for (const row of rows.slice(1)) {
          if (row.closest('thead') || row.cells.length !== column) continue;
          for (const sizeColumn of sizeColumns) {
            const size = row.cells[sizeColumn];
            if (!size || !sizeValue.test(text(size))) continue;
            if (measureColumns.some((index) => index !== sizeColumn && row.cells[index] && numeric(text(row.cells[index])))) sizes.add(text(size).toUpperCase());
          }
        }
        if (sizes.size < 2) continue;
        const context = [];
        let previous = table.previousElementSibling;
        for (let parent = table.parentElement; !previous && parent !== description && description.contains(parent); parent = parent.parentElement) previous = parent.previousElementSibling;
        let guideStart = -1;
        for (let count = 0; previous && count < 4; count += 1, previous = previous.previousElementSibling) {
          if (previous.matches('table, img, picture, video, iframe, script, style') || previous.querySelector('table, img, picture, video, iframe, script, style')) break;
          if (previous.matches('h1, h2, h3, h4, h5, h6') && !guideLabel.test(text(previous))) break;
          context.push(previous);
          if (guideLabel.test(text(previous))) guideStart = context.length - 1;
        }
        for (const node of context.slice(0, guideStart + 1).reverse()) {
          if (!copiedContext.has(node)) { nodes.push(node.cloneNode(true)); copiedContext.add(node); }
        }
        nodes.push(table.cloneNode(true));
      }
      if (!nodes.length) { this.sizeGuideTrigger.hidden = true; return; }
      for (const node of nodes) {
        node.removeAttribute('id');
        for (const child of node.querySelectorAll('[id]')) child.removeAttribute('id');
      }
      content.replaceChildren(...nodes);
      this.sizeGuideTrigger.hidden = false;
      this.sizeGuide.addEventListener('close', () => {
        this.sizeGuideTrigger.setAttribute('aria-expanded', 'false');
        if (this.sizeGuideTrigger.isConnected) this.sizeGuideTrigger.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
    }

    openSizeGuide() {
      if (!this.sizeGuide || this.sizeGuideTrigger?.hidden || this.sizeGuide.open) return;
      this.sizeGuide.showModal();
      this.sizeGuideTrigger.setAttribute('aria-expanded', 'true');
      this.sizeGuide.querySelector('[data-or-size-guide-close]')?.focus({ preventScroll: true });
    }

    syncSelectionScarcity() {
      const badge = this.querySelector('[data-or-selection-scarcity]');
      if (!badge) return;
      badge.hidden = !this.selectedVariant?.available || !this.choices?.every((choice) => choice !== null && choice !== undefined);
      const home = this.querySelector('[data-or-selection-scarcity-home]');
      const sizeLegend = this.enhancedOptions?.querySelector(`[data-or-option-group="${this.sizeIndex}"] legend`);
      if (sizeLegend) sizeLegend.dataset.orSelectionScarcityLegend = '';
      const destination = this.optionSheet?.open ? sizeLegend : home;
      if (destination && badge.parentElement !== destination) destination.append(badge);
    }

    setupOptionSheet() {
      this.optionSheetReady = false;
      this.optionSheet = this.querySelector('[data-or-option-sheet]');
      this.optionHome = this.querySelector('[data-or-options-home]');
      this.enhancedOptions = this.querySelector('[data-or-enhanced-options]');
      this.optionTrigger = this.querySelector('[data-or-option-sheet-open]');
      this.sheetOptions = this.querySelector('[data-or-sheet-options]');
      if (!this.optionSheet || !this.optionHome || !this.enhancedOptions || !this.optionTrigger || !this.sheetOptions) return;
      if (!this.enhancedOptions.querySelector('[data-or-option-value]')) {
        this.optionTrigger.hidden = true;
        this.optionHome.hidden = true;
        return;
      }
      for (const button of this.enhancedOptions.querySelectorAll(`.or-product__text-choice[data-or-option-index="${this.sizeIndex}"]`)) {
        if (!button.closest('.or-product__size-row')) {
          const row = document.createElement('div');
          row.className = 'or-product__size-row';
          button.before(row);
          row.append(button);
        }
        if (!button.querySelector('[data-or-option-price]')) {
          const price = document.createElement('span');
          price.dataset.orOptionPrice = '';
          button.append(price);
        }
      }
      this.optionSheet.addEventListener('cancel', (event) => { event.preventDefault(); this.closeOptionSheet(); }, { signal: this.abort.signal });
      this.optionSheet.addEventListener('close', () => {
        this.sheetClosing = false;
        delete this.optionSheet.dataset.closing;
        this.syncSelectionScarcity();
        this.optionTrigger.setAttribute('aria-expanded', 'false');
        const target = this.sheetReturnTarget;
        this.sheetReturnTarget = null;
        if (!document.querySelector('dialog[open]') && target?.isConnected) {
          const destination = !target.hidden && !target.disabled ? target : this.querySelector('[data-or-back]');
          destination?.focus({ preventScroll: true });
        }
      }, { signal: this.abort.signal });
      this.optionSheet.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = [...this.optionSheet.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((node) => node.getClientRects().length > 0);
        const destination = event.shiftKey && document.activeElement === controls[0] ? controls.at(-1) : !event.shiftKey && document.activeElement === controls.at(-1) ? controls[0] : null;
        if (destination) { event.preventDefault(); destination.focus({ preventScroll: true }); }
      }, { signal: this.abort.signal });
      this.optionSheetReady = true;
      this.syncOptionLayout();
    }

    syncOptionLayout() {
      if (!this.optionSheetReady) return;
      this.sheetOptions.append(this.enhancedOptions);
      const quantity = this.querySelector('.or-product__quantity');
      if (quantity) this.sheetOptions.append(quantity);
      this.dataset.optionSheet = 'true';
      this.optionTrigger.hidden = false;
      this.syncColorRail();
    }

    openOptionSheet(trigger) {
      if (!this.optionSheetReady || this.busy || this.uncertain || this.optionSheet.open) return;
      this.sheetReturnTarget = trigger;
      this.optionSheet.showModal();
      this.syncSelectionScarcity();
      this.optionTrigger.setAttribute('aria-expanded', 'true');
      this.syncColorRail();
      const chosenSize = this.sizeIndex >= 0 ? this.enhancedOptions.querySelector(`[data-or-option-index="${this.sizeIndex}"][aria-pressed="true"]:not([disabled])`) : null;
      (this.firstMissingOption() || chosenSize || this.enhancedOptions.querySelector('button:not([disabled])'))?.focus({ preventScroll: true });
    }

    syncQuantityPlacement() {
      this.syncSelectionScarcity();
      if (!this.optionSheetReady) return;
      const quantity = this.querySelector('.or-product__quantity');
      if (quantity) quantity.hidden = !this.selectedVariant;
      const chosenSize = this.selectedVariant && this.enhancedOptions.querySelector(`.or-product__text-choice[data-or-option-index="${this.sizeIndex}"][aria-pressed="true"]`);
      const row = !matchMedia('(max-width: 640px)').matches ? chosenSize?.closest('.or-product__size-row') : null;
      if (quantity && quantity.parentElement !== (row || this.sheetOptions)) (row || this.sheetOptions).append(quantity);
    }

    closeOptionSheet() {
      if (!this.optionSheet?.open || this.sheetClosing) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.optionSheet.close(); return; }
      this.sheetClosing = true;
      this.optionSheet.dataset.closing = 'true';
      Promise.all(this.optionSheet.getAnimations().map((animation) => animation.finished.catch(() => {}))).then(() => {
        if (this.sheetClosing && this.optionSheet.open) this.optionSheet.close();
      });
    }

    render() {
      const displayed = this.displayVariant();
      for (const button of this.querySelectorAll('[data-or-option-index]')) {
        const index = Number(button.dataset.orOptionIndex);
        const value = button.dataset.orOptionValue;
        button.setAttribute('aria-pressed', String(this.choices[index] === value));
        button.disabled = this.busy || this.uncertain || !this.optionAvailable(index, value);
        const optionPrice = button.querySelector('[data-or-option-price]');
        if (optionPrice) {
          const candidate = this.core.match(this.variants, this.choices.map((choice, optionIndex) => optionIndex === index ? value : choice));
          optionPrice.textContent = candidate ? this.format(candidate.price) : '';
          optionPrice.hidden = !candidate || this.choices[index] === value;
        }
      }
      for (const label of this.querySelectorAll('[data-or-option-label]')) {
        const index = Number(label.dataset.orOptionLabel);
        label.textContent = this.choices[index] ? this.optionLabel.display(this.choices[index]) : 'Nog kiezen';
      }
      const price = this.querySelector('[data-or-main-price]');
      if (price) price.textContent = this.format(displayed.price);
      const sku = this.querySelector('[data-or-sku]');
      if (sku) sku.textContent = displayed.sku || '';
      const skuGroup = this.querySelector('[data-or-sku-group]');
      if (skuGroup) skuGroup.hidden = !String(displayed.sku || '').trim();
      const meta = this.querySelector('[data-or-product-meta]');
      if (meta) meta.hidden = this.colorIndex < 0 && !String(displayed.sku || '').trim();
      const color = this.querySelector('[data-or-color-label]');
      if (color && this.colorIndex >= 0) color.textContent = this.optionLabel.display(this.choices[this.colorIndex]);
      const totalCents = this.core.total(this.selectedVariant, this.selectedQuantity);
      for (const total of this.querySelectorAll('[data-or-primary-total]')) { total.hidden = !this.selectedVariant; total.textContent = this.selectedVariant ? this.format(totalCents) : ''; }
      const quantityOutput = this.querySelector('[data-or-quantity-output]');
      if (quantityOutput) quantityOutput.textContent = String(this.selectedQuantity);
      for (const button of this.querySelectorAll('[data-or-quantity]')) button.disabled = this.busy || this.uncertain || (Number(button.dataset.orQuantity) < 0 ? this.selectedQuantity <= 1 : this.selectedQuantity >= 99);
      const idInput = this.querySelector('[data-or-variant-input]');
      const quantityInput = this.querySelector('[data-or-quantity-input]');
      if (idInput) { idInput.value = this.selectedVariant?.id || ''; idInput.disabled = !this.selectedVariant; }
      if (quantityInput) { quantityInput.value = String(this.selectedQuantity); quantityInput.disabled = !this.selectedVariant; }
      const matched = this.core.match(this.variants, this.choices);
      const unavailable = matched ? !matched.available : !this.variants.some((variant) => variant.available);
      const selectionReady = Boolean(this.selectedVariant?.available && matched?.id === this.selectedVariant.id);
      for (const primary of this.querySelectorAll('[data-or-primary]')) { primary.disabled = this.busy || this.uncertain || unavailable || (primary.hasAttribute('data-or-sheet-add') && !selectionReady); primary.setAttribute('aria-busy', String(this.busy)); }
      this.renderPrimaryLabels(unavailable);
      for (const cartLink of this.querySelectorAll('[data-or-cart-link]')) cartLink.hidden = !this.added && !this.uncertain;
      if (this.optionTrigger) this.optionTrigger.disabled = this.busy || this.uncertain;
      const summary = this.querySelector('[data-or-option-summary]');
      if (summary) summary.textContent = this.config.options.map((name, index) => `${name}: ${this.choices[index] ? this.optionLabel.display(this.choices[index]) : 'Nog kiezen'}`).join(' · ');
      this.dataset.added = String(this.added);
      this.renderMedia();
      this.renderFavorite();
      this.syncQuantityPlacement();
      this.syncColorRail();
      this.renderTier();
      this.recordVisit(displayed);
    }

    renderPrimaryLabels(unavailable) {
      for (const label of this.querySelectorAll('[data-or-primary-label]')) label.textContent = this.busy ? this.config.strings.adding : unavailable ? this.config.strings.unavailable : this.addFeedback ? this.config.strings.added : this.selectedVariant ? this.config.strings.add : this.config.strings.chooseVariant;
    }

    confirmedTier(cart) {
      const tiers = [
        { quantity: 2, percent: 10, title: '2 artikelen · 10% korting' },
        { quantity: 3, percent: 15, title: '3 artikelen · 15% korting' },
        { quantity: 4, percent: 20, title: '4 artikelen · 20% korting' },
        { quantity: 5, percent: 25, title: '5+ artikelen · 25% korting' },
      ];
      if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) return null;
      const allocations = cart.items.flatMap((line) => line.line_level_discount_allocations || line.discount_allocations || []);
      const applications = [...allocations.map((allocation) => ({ title: allocation.discount_application?.title, amount: allocation.amount })),
        ...(cart.cart_level_discount_applications || []).map((application) => ({ title: application.title, amount: application.total_allocated_amount }))];
      return tiers.filter((tier) => cart.item_count >= tier.quantity && applications.some((application) => application.title === tier.title && Number(application.amount) > 0)).at(-1) || null;
    }

    tierText(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.config.strings[key] || '');
    }

    renderTier() {
      const status = this.querySelector('[data-or-tier-status]');
      if (!status) return;
      const rows = [...this.querySelectorAll('[data-or-tier-quantity]')];
      const confirmed = this.confirmedTier(this.tierCart);
      const count = Number.isInteger(this.tierCart?.item_count) && this.tierCart.item_count >= 0 ? this.tierCart.item_count : null;
      const canEstimate = !this.tierAdded && !this.tierCartPending && !this.busy && !this.uncertain && this.selectedVariant?.available && count !== null;
      const projectedCount = canEstimate ? count + this.selectedQuantity : null;
      const projected = canEstimate ? rows.filter((row) => projectedCount >= Number(row.dataset.orTierQuantity)).at(-1) : null;
      const useEstimate = Boolean(projected && (!confirmed || this.tierSelectionChanged));
      const showingEstimate = useEstimate && !confirmed;
      const activeQuantity = this.tierCartPending || this.busy || this.uncertain ? null : confirmed?.quantity ?? (showingEstimate ? Number(projected.dataset.orTierQuantity) : null);
      for (const row of rows) {
        const active = Number(row.dataset.orTierQuantity) === activeQuantity;
        row.dataset.state = active ? showingEstimate ? 'estimate' : 'confirmed' : 'inactive';
        const check = row.querySelector('[data-or-tier-check]');
        const state = row.querySelector('[data-or-tier-state]');
        if (check) check.toggleAttribute('hidden', !active || showingEstimate);
        if (state) { state.hidden = !active; state.textContent = this.tierText(showingEstimate ? 'tierEstimatedState' : 'tierConfirmedState'); }
      }
      let message = '';
      if (this.tierCartPending) message = this.tierText('tierWaiting');
      else if (this.uncertain) message = this.tierText('tierUncertain');
      else if (confirmed) message = this.tierText('tierConfirmed', { count, percent: confirmed.percent });
      else if (showingEstimate) message = this.tierText('tierEstimate', { count: projectedCount, percent: projected.dataset.orTierPercent });
      else if (this.tierAdded) message = this.tierText('tierUnconfirmed');
      status.textContent = message;
      status.hidden = message.length === 0;
    }

    syncColorRail() {
      const track = this.querySelector('[data-or-color-track]');
      if (!track) return;
      const overflow = track.scrollWidth > track.clientWidth + 2;
      track.dataset.overflow = String(overflow);
      const previous = this.querySelector('[data-or-color-previous]');
      const next = this.querySelector('[data-or-color-next]');
      if (previous) previous.hidden = !overflow || track.scrollLeft <= 2;
      if (next) next.hidden = !overflow || Math.ceil(track.scrollLeft + track.clientWidth) >= track.scrollWidth - 2;
      if (!track.dataset.bound) {
        track.dataset.bound = 'true';
        track.addEventListener('scroll', () => this.syncColorRail(), { passive: true, signal: this.abort.signal });
      }
    }

    scrollColors(direction) {
      const track = this.querySelector('[data-or-color-track]');
      if (!track) return;
      track.scrollBy({ left: direction * Math.max(77, track.clientWidth - 77), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    setupMediaFallback() {
      const figureFor = (image) => {
        if (image?.tagName !== 'IMG') return null;
        const figure = image.closest('[data-or-media]');
        return figure && this.contains(figure) && figure.dataset.orMediaType === 'image' ? figure : null;
      };
      const failed = (figure) => {
        const key = this.core.mediaKey(figure.dataset.orMediaSource || figure.dataset.orMedia);
        if (this.failedMediaKeys.has(key)) return;
        this.failedMediaKeys.add(key);
        this.mediaSignature = null;
        this.renderMedia();
      };
      this.addEventListener('error', (event) => {
        const figure = figureFor(event.target);
        if (figure) failed(figure);
      }, { capture: true, signal: this.abort.signal });
      this.addEventListener('load', (event) => {
        const figure = figureFor(event.target);
        if (!figure || event.target.naturalWidth <= 0) return;
        const key = this.core.mediaKey(figure.dataset.orMediaSource || figure.dataset.orMedia);
        if (this.failedMediaKeys.delete(key)) {
          this.mediaSignature = null;
          this.renderMedia();
        }
      }, { capture: true, signal: this.abort.signal });
      for (const figure of this.querySelectorAll('[data-or-media]')) {
        const image = figure.querySelector('img');
        if (figure.dataset.orMediaType === 'image' && image?.getAttribute('src') && image.complete && image.naturalWidth === 0) failed(figure);
      }
    }

    renderMedia() {
      const selectedColor = this.colorIndex >= 0 ? this.choices[this.colorIndex] : null;
      const items = [...this.querySelectorAll('[data-or-media]')];
      const failed = this.failedMediaKeys ||= new Set();
      const mediaKey = (item) => item.dataset.orMediaType === 'image' ? this.core.mediaKey(item.dataset.orMediaSource || item.dataset.orMedia) : item.dataset.orMedia;
      const availableImages = items.filter((item) => {
        const image = item.querySelector('img');
        return item.dataset.orMediaType === 'image' && Boolean(image?.getAttribute('src') || image?.getAttribute('srcset')) && !failed.has(mediaKey(item));
      });
      let configured = this.core.mediaIds({
        allMediaIds: items.map((media) => media.dataset.orMedia),
        selectedVariant: this.displayVariant(),
        selectedColor,
        variantMediaById: this.config.variantMediaById,
        mediaByColor: this.config.mediaByColor,
      });
      const thumbnailMedia = [...this.querySelectorAll('[data-or-color-thumbnail]')].find((button) => button.dataset.orOptionValue === selectedColor)?.dataset.orColorMedia;
      if (thumbnailMedia && availableImages.some((item) => item.dataset.orMedia === thumbnailMedia)) configured = [thumbnailMedia, ...configured.filter((id) => String(id) !== thumbnailMedia)];
      const displayed = this.displayVariant();
      const mappedIds = [this.config.variantMediaById?.[String(displayed?.id)], ...(this.config.mediaByColor?.[selectedColor] || [])];
      const hasColorMedia = mappedIds.some((id) => id != null && availableImages.some((item) => String(item.dataset.orMedia) === String(id)));
      const general = availableImages.find((item) => String(item.dataset.orMedia) === String(this.config.generalMediaId)) || availableImages[0];
      const fallbackNeeded = !hasColorMedia && (Boolean(selectedColor) || failed.size > 0);
      if (fallbackNeeded && general) configured = [general.dataset.orMedia, ...configured.filter((id) => String(id) !== general.dataset.orMedia)];
      configured = configured.filter((id) => {
        const item = items.find((media) => String(media.dataset.orMedia) === String(id));
        return item && !failed.has(mediaKey(item)) && (item.dataset.orMediaType !== 'image' || availableImages.includes(item));
      });
      const note = this.querySelector('[data-or-media-note]');
      if (note) {
        note.textContent = availableImages.length ? this.genericMediaNote : this.config.strings.photoUnavailable || '';
        note.hidden = availableImages.length ? !fallbackNeeded : !note.textContent;
      }
      const signature = JSON.stringify([selectedColor, configured, [...failed]]);
      if (signature === this.mediaSignature) return;
      this.mediaSignature = signature;
      const keyById = new Map();
      const canonical = new Map();
      for (const media of items) {
        const key = mediaKey(media);
        keyById.set(media.dataset.orMedia, key);
        if (!canonical.has(key) && (media.dataset.orMediaType !== 'image' || availableImages.includes(media))) canonical.set(key, media.dataset.orMedia);
      }
      const allowed = new Set((configured || []).map(String).map((id) => keyById.get(id)).filter(Boolean));
      const filter = allowed.size > 0;
      for (const media of items) {
        const id = media.dataset.orMedia;
        const key = keyById.get(id);
        media.hidden = failed.has(key) || (media.dataset.orMediaType === 'image' && !availableImages.includes(media)) || canonical.get(key) !== id || (filter && !allowed.has(key));
      }
      for (const dot of this.querySelectorAll('[data-or-dot]')) dot.hidden = this.querySelector(`[data-or-media="${CSS.escape(dot.dataset.orDot)}"]`)?.hidden ?? true;
      const preferredKey = keyById.get(configured[0]);
      const first = items.find((media) => !media.hidden && keyById.get(media.dataset.orMedia) === preferredKey) || this.querySelector('[data-or-media]:not([hidden])');
      if (first) {
        for (const dot of this.querySelectorAll('[data-or-dot]')) dot.dataset.current = String(dot.dataset.orDot === first.dataset.orMedia);
        const track = this.querySelector('[data-or-media-track]');
        if (track && Math.abs(track.scrollLeft - first.offsetLeft) > 2) track.scrollTo({ left: first.offsetLeft, behavior: 'auto' });
      }
      if (items.some((item) => item.hidden && item.contains(document.activeElement))) {
        const nextFocus = first?.querySelector('[data-or-zoom-open]') || this.querySelector('[data-or-media-track]');
        nextFocus?.focus({ preventScroll: true });
      }
    }

    onMediaKeydown(event) {
      const track = event.currentTarget;
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || track.scrollWidth <= track.clientWidth + 2) return;
      const media = [...track.querySelectorAll('[data-or-media]:not([hidden])')];
      if (media.length < 2) return;
      const origin = track.getBoundingClientRect().left;
      const positions = media.map((item) => item.getBoundingClientRect().left - origin + track.scrollLeft);
      const current = positions.reduce((best, left, index) => Math.abs(left - track.scrollLeft) < Math.abs(positions[best] - track.scrollLeft) ? index : best, 0);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? media.length - 1 : Math.max(0, Math.min(media.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
      event.preventDefault();
      track.scrollTo({ left: positions[next], behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    firstMissingOption() {
      const index = this.choices.findIndex((value) => !value);
      return index < 0 ? null : this.enhancedOptions?.querySelector(`[data-or-option-index="${index}"]:not([disabled])`);
    }

    async add(trigger = document.activeElement) {
      if (this.busy || this.uncertain) return;
      const returnTarget = this.optionSheet?.contains(trigger) ? this.sheetReturnTarget || this.optionTrigger : trigger;
      if (!this.selectedVariant) {
        this.status(this.config.strings.chooseVariant, true);
        if (this.optionSheet && !this.optionSheet.open) this.openOptionSheet(this.optionTrigger);
        this.firstMissingOption()?.focus({ preventScroll: true });
        return;
      }
      const item = this.core.item(this.selectedVariant, this.selectedQuantity);
      const drawerInteraction = document.querySelector('oluvie-cart-drawer')?.captureAddInteraction?.();
      let baseline;
      let submitted = false;
      const cartSyncGeneration = ++this.cartSyncGeneration;
      this.addFeedback = false;
      clearTimeout(this.addFeedbackTimer);
      this.busy = true; this.status(this.config.strings.adding); this.render();
      const controller = new AbortController();
      let timeout;
      try {
        const before = await this.getCart(5000);
        baseline = this.core.cartQuantity(before.items, item.id);
        this.cartQuantities.set(item.id, baseline);
        submitted = true;
        timeout = setTimeout(() => controller.abort(), 20000);
        const response = await fetch(this.config.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }), signal: controller.signal });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) { const failure = new Error(body.description || this.config.strings.addError); failure.httpRejection = response.status >= 400 && response.status < 500; throw failure; }
        const cartRequest = this.getCart(5000);
        void cartRequest.catch(() => {});
        this.completeAdd(item, body, null, baseline + item.quantity, returnTarget, drawerInteraction, cartRequest);
        void this.refreshCartAfterAdd(item, cartSyncGeneration, cartRequest).catch(() => {});
      } catch (error) {
        if (!submitted) { this.status(this.config.strings.networkError, true); return; }
        const cart = await this.getCart(5000).catch(() => null);
        const quantity = cart ? this.core.cartQuantity(cart.items, item.id) : null;
        if (cart && quantity >= baseline + item.quantity) this.completeAdd(item, null, cart, quantity, returnTarget, drawerInteraction);
        else if (error.httpRejection && cart && quantity === baseline) {
          this.cartQuantities.set(item.id, quantity);
          this.syncCart(cart);
          this.uncertain = false;
          this.status(error.message, true);
        } else {
          this.uncertain = true;
          if (cart) { this.cartQuantities.set(item.id, quantity); this.syncCart(cart); }
          this.status(this.config.strings.cartStateUncertain || this.config.strings.networkError, true);
        }
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        if (this.pendingHistorySelection) {
          const pending = this.pendingHistorySelection;
          this.pendingHistorySelection = null;
          this.restoreSelection(pending.state);
        } else this.render();
      }
    }

    async getCart(timeoutMs = 5000) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(this.config.cartGetUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error(this.config.strings.networkError);
        const cart = await response.json();
        if (!Array.isArray(cart?.items) || cart.items.some((line) => !Number.isFinite(Number(line.quantity)) || Number(line.quantity) < 0)) throw new Error(this.config.strings.networkError);
        return cart;
      } finally {
        clearTimeout(timeout);
      }
    }

    async refreshCartAfterAdd(item, generation, cartRequest) {
      const tierVersion = this.tierCartVersion;
      const cart = await (cartRequest || this.getCart(5000)).catch(() => null);
      if (generation !== this.cartSyncGeneration || tierVersion !== this.tierCartVersion || !this.isConnected) return;
      if (!cart) {
        this.tierCart = null;
        this.tierCartPending = false;
        this.renderTier();
        return;
      }
      const quantity = this.core.cartQuantity(cart.items, item.id);
      if (Number.isFinite(quantity)) this.cartQuantities.set(item.id, quantity);
      this.syncCart(cart);
    }

    syncCart(cart) {
      const count = cart?.item_count;
      if (!Number.isFinite(count)) return;
      this.tierCart = cart;
      this.tierCartPending = false;
      this.renderTier();
      for (const node of document.querySelectorAll('[data-or-cart-count]')) {
        node.textContent = String(count);
        node.hidden = count < 1;
      }
      const mobileCount = this.querySelector('[data-or-mobile-cart-count]');
      if (mobileCount) { mobileCount.textContent = String(count); mobileCount.hidden = count < 1; }
      for (const link of document.querySelectorAll('.or-header__cart')) {
        const label = link.querySelector(':scope > span')?.textContent || 'Winkelmand';
        link.setAttribute('aria-label', `${label}, ${count}`);
      }
    }

    completeAdd(item, body, cart, quantity, trigger, drawerInteraction, cartRequest) {
      this.cartQuantities.set(item.id, quantity);
      this.added = true;
      this.addFeedback = true;
      clearTimeout(this.addFeedbackTimer);
      this.addFeedbackTimer = setTimeout(() => {
        if (!this.isConnected) return;
        this.addFeedback = false;
        const matched = this.core.match(this.variants, this.choices);
        this.renderPrimaryLabels(matched ? !matched.available : !this.variants.some((variant) => variant.available));
      }, 2200);
      this.tierAdded = true;
      this.tierCartPending = !Array.isArray(cart?.items);
      this.status(this.config.strings.added);
      const link = this.querySelector('[data-or-cart-link]');
      if (link) link.hidden = false;
      if (this.optionSheet?.open) { this.sheetReturnTarget = null; this.optionSheet.close(); }
      this.dispatchEvent(new CustomEvent('oluvie:cart-added', { bubbles: true, detail: { item, body, cart, trigger, drawerInteraction, cartRequest } }));
      this.syncCart(cart);
    }

    status(message, error = false) {
      for (const node of this.querySelectorAll('[data-or-status]')) {
        node.textContent = message;
        node.dataset.error = String(Boolean(error));
        if (node.closest('.or-product__feedback--purchase')) node.classList.toggle('visually-hidden', !error);
        node.setAttribute('aria-live', error ? 'assertive' : 'polite');
      }
    }

    recordVisit(variant) {
      const identity = `${String(this.config.id)}:${String(variant?.id ?? '')}`;
      if (identity === this.productViewIdentity) return;
      this.productViewIdentity = identity;
      const media = this.querySelector('[data-or-media]:not([hidden]) img');
      const detail = { product: { id: this.config.id, handle: this.config.handle, title: this.config.title, url: this.config.productUrl, image: media?.currentSrc || media?.src || this.config.initialImage, hasOnlyDefaultVariant: this.config.hasOnlyDefaultVariant === true }, variant: variant ? { id: variant.id, title: variant.title, options: variant.options, price: variant.price } : null };
      this.dispatchEvent(new CustomEvent('oluvie:product-view', { bubbles: true, detail }));
    }

    openZoom(trigger) {
      const visible = [...this.querySelectorAll('[data-or-media]:not([hidden])')].map((figure) => {
        const galleryImage = figure.querySelector('img');
        const image = figure.querySelector('[data-or-zoom-source]')?.content.querySelector('img') || galleryImage;
        return image ? { id: figure.dataset.orMedia, src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), sizes: image.getAttribute('sizes'), width: image.getAttribute('width'), height: image.getAttribute('height'), alt: image.alt } : null;
      }).filter(Boolean);
      if (!visible.length) return;
      this.zoomItems = visible; this.zoomIndex = Math.max(0, visible.findIndex((item) => item.id === trigger.dataset.orZoomOpen)); this.zoomTrigger = trigger; this.renderZoom();
      const dialog = this.querySelector('[data-or-zoom]'); if (!dialog.open) dialog.showModal(); dialog.querySelector('[data-or-zoom-close]')?.focus({ preventScroll: true });
    }

    stepZoom(delta) { if (this.zoomItems?.length) { this.zoomIndex = (this.zoomIndex + delta + this.zoomItems.length) % this.zoomItems.length; this.renderZoom(); } }
    renderZoom() {
      const item = this.zoomItems?.[this.zoomIndex];
      if (!item) return;
      const image = this.querySelector('[data-or-zoom-image]');
      for (const name of ['sizes', 'srcset', 'width', 'height']) {
        if (item[name]) {
          if (image.getAttribute(name) !== String(item[name])) image.setAttribute(name, item[name]);
        } else if (image.hasAttribute(name)) image.removeAttribute(name);
      }
      if (image.getAttribute('src') !== item.src) image.src = item.src;
      if (image.alt !== item.alt) image.alt = item.alt;
      const multiple = this.zoomItems.length > 1;
      this.querySelector('[data-or-zoom-previous]').hidden = !multiple;
      this.querySelector('[data-or-zoom-next]').hidden = !multiple;
    }

    observeMedia() {
      if (!('IntersectionObserver' in window)) return;
      this.mediaObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting && !entry.target.hidden).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        for (const dot of this.querySelectorAll('[data-or-dot]')) dot.dataset.current = String(dot.dataset.orDot === visible.target.dataset.orMedia);
      }, { threshold: [0.5, 0.75] });
      for (const media of this.querySelectorAll('[data-or-media]')) this.mediaObserver.observe(media);
    }

    async share() {
      try { if (navigator.share) await navigator.share({ title: this.config.title, url: window.location.href }); else { await navigator.clipboard.writeText(window.location.href); this.status(this.config.strings.linkCopied); } }
      catch (error) { if (error.name !== 'AbortError') this.status(this.config.strings.shareError, true); }
    }
  });
})();
